'use client';

import {
	EventMap,
	OrderAction,
	WrappedEvent,
	ZERO,
} from '@velocity-exchange/sdk';
import { useCallback, useRef } from 'react';
import { ENUM_UTILS, MarketId } from '@velocity-exchange/common';
import useVelocityEventBuffer, {
	VelocityEventBuffer,
} from './useVelocityEventBuffer';
import useVelocityStore from '../../stores/VelocityStore/useVelocityStore';

const INCREASING_SLOTS_ONLY = true;

export const fillEventFilter = (
	velocityEvent: WrappedEvent<keyof EventMap>,
	opts?: {
		marketId?: MarketId;
	}
) => {
	if (velocityEvent.eventType !== 'OrderActionRecord') {
		return false;
	}

	const typedEvent = velocityEvent as WrappedEvent<'OrderActionRecord'>;

	if (opts?.marketId) {
		if (
			!opts.marketId.equals(
				new MarketId(typedEvent.marketIndex, typedEvent.marketType)
			)
		) {
			return false;
		}
	}

	if (!ENUM_UTILS.match(typedEvent.action, OrderAction.FILL)) {
		return false;
	}

	if (typedEvent.baseAssetAmountFilled?.eq(ZERO)) {
		return false;
	}

	return true;
};

const useFillEventsBuffer = (opts?: {
	currentMarketOnly?: boolean;
	customFilter?: (event: WrappedEvent<'OrderActionRecord'>) => boolean;
}) => {
	const lastSeenSlot = useRef(0);

	const currentMarketId = useVelocityStore((s) => s.selectedMarket.marketId);

	const lastSeenSlotFilter = (velocityEvent: WrappedEvent<keyof EventMap>) => {
		if (!INCREASING_SLOTS_ONLY) {
			return true;
		}

		if (lastSeenSlot.current > velocityEvent.slot) {
			return false;
		}

		return true;
	};

	const filteringFunc = useCallback(
		(velocityEvent: WrappedEvent<keyof EventMap>) => {
			// Base Filter
			const baseFilterResult = opts?.currentMarketOnly
				? fillEventFilter(velocityEvent, { marketId: currentMarketId })
				: fillEventFilter(velocityEvent);

			if (!baseFilterResult) {
				return false;
			}

			// Slot Filter
			const slotFilterResult = lastSeenSlotFilter(velocityEvent);

			if (!slotFilterResult) {
				return false;
			}

			// Custom Filter
			const typedEvent = velocityEvent as WrappedEvent<'OrderActionRecord'>;

			const customFilterResult = opts?.customFilter
				? opts.customFilter(typedEvent)
				: true;

			if (!customFilterResult) {
				return false;
			}

			return true;
		},
		[currentMarketId, opts?.currentMarketOnly]
	);

	const eventBuffer = useVelocityEventBuffer(
		filteringFunc
	) as VelocityEventBuffer<WrappedEvent<'OrderActionRecord'>>;

	return eventBuffer;
};

export default useFillEventsBuffer;
